import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PromoTab = ({ promotions, cashbackOffers }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getDaysRemaining = (endDate) => {
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="space-y-6 md:space-y-8 lg:space-y-10">
      {promotions?.length > 0 && (
        <section>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground mb-4 md:mb-6">
            Promozioni Attive
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            {promotions?.map((promo) => {
              const daysLeft = getDaysRemaining(promo?.validUntil);
              return (
                <div
                  key={promo?.id}
                  className="bg-card rounded-lg md:rounded-xl lg:rounded-2xl overflow-hidden shadow-warm"
                >
                  <div className="relative h-40 md:h-48 lg:h-56 overflow-hidden">
                    <Image
                      src={promo?.image}
                      alt={promo?.imageAlt}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-3 right-3 px-3 py-1.5 rounded-full bg-accent text-accent-foreground text-xs md:text-sm font-caption font-semibold">
                      {promo?.discount}
                    </div>
                  </div>
                  <div className="p-4 md:p-6">
                    <h3 className="text-lg md:text-xl font-heading font-bold text-foreground mb-2">
                      {promo?.title}
                    </h3>
                    <p className="text-sm md:text-base text-muted-foreground mb-4 line-clamp-2">
                      {promo?.description}
                    </p>

                    <div className="space-y-3 mb-4">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Icon name="Calendar" size={16} />
                        <span>Valido fino al {formatDate(promo?.validUntil)}</span>
                      </div>
                      {daysLeft <= 7 && (
                        <div className="flex items-center gap-2 text-sm text-warning">
                          <Icon name="Clock" size={16} />
                          <span className="font-caption font-semibold">
                            {daysLeft === 0 ? 'Scade oggi!' : `${daysLeft} giorni rimanenti`}
                          </span>
                        </div>
                      )}
                    </div>

                    {promo?.terms && (
                      <div className="p-3 bg-muted rounded-lg mb-4">
                        <p className="text-xs md:text-sm text-muted-foreground">
                          <span className="font-caption font-semibold">Termini: </span>
                          {promo?.terms}
                        </p>
                      </div>
                    )}

                    <Button
                      variant="default"
                      size="default"
                      iconName="Tag"
                      iconPosition="left"
                      className="w-full"
                    >
                      Attiva Promozione
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
      {cashbackOffers?.length > 0 && (
        <section>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground mb-4 md:mb-6">
            Cashback Disponibile
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {cashbackOffers?.map((cashback) => (
              <div
                key={cashback?.id}
                className="bg-card rounded-lg md:rounded-xl p-4 md:p-6 shadow-warm border-2 border-success/20"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-lg bg-success/10 flex items-center justify-center">
                    <Icon name="Coins" size={24} color="var(--color-success)" />
                  </div>
                  <span className="text-2xl md:text-3xl font-heading font-bold text-success">
                    {cashback?.percentage}%
                  </span>
                </div>

                <h3 className="text-base md:text-lg font-heading font-bold text-foreground mb-2">
                  {cashback?.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {cashback?.description}
                </p>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Spesa minima:</span>
                    <span className="font-caption font-semibold text-foreground">
                      €{cashback?.minSpend?.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Cashback max:</span>
                    <span className="font-caption font-semibold text-success">
                      €{cashback?.maxCashback?.toFixed(2)}
                    </span>
                  </div>
                </div>

                <Button
                  variant="success"
                  size="default"
                  iconName="Wallet"
                  iconPosition="left"
                  className="w-full"
                >
                  Attiva Cashback
                </Button>
              </div>
            ))}
          </div>
        </section>
      )}
      {promotions?.length === 0 && cashbackOffers?.length === 0 && (
        <div className="bg-card rounded-lg md:rounded-xl lg:rounded-2xl p-8 md:p-12 text-center shadow-warm">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
            <Icon name="Tag" size={32} className="text-muted-foreground" />
          </div>
          <h3 className="text-lg md:text-xl font-heading font-bold text-foreground mb-2">
            Nessuna Promozione Attiva
          </h3>
          <p className="text-sm md:text-base text-muted-foreground">
            Al momento non ci sono promozioni o cashback disponibili per questa attività.
          </p>
        </div>
      )}
    </div>
  );
};

export default PromoTab;